/* Pack-open reveal engine — a self-contained port of the arena (index v3) "PACK"
 * module, reworked into a Pokémon-TCG-Pocket-style STACKED SWIPE reveal: the cards
 * sit on top of each other and you drag left/right (the top card tilts in 3D so you
 * see its edge + foil) and flick it away to reveal the next. Order is random EXCEPT
 * relic cards ride at the very back — the exciting finale. After the last swipe the
 * whole pull is shown as a face-up summary grid (tap any card to enlarge + tilt).
 *
 * It is handed the ACTUAL claimed cards (no random generation); each front shows only
 * art + name + rarity (a claim carries no atk/hp/ability).
 *
 * Vanilla on purpose: imperative DOM + a particle canvas. It runs inside a container
 * the React overlay owns and is torn down on unmount; all lookups are scoped to it. */

import { INCRYPT_MARK } from './packRevealAssets';

export type RevealCard = {
  name: string;
  tier: number; // 0 common … 4 relic
  art: string;
  rarity?: string;
  /* estimated value (the average active marketplace listing, as the collection page
     counts it): INC and USD; null / absent when the card has no listing yet */
  valueInc?: number | null;
  valueUsd?: number | null;
};

export type PackRevealHandle = { destroy: () => void };
/* what the haul can share: the public page's URL (server lib/packPulls) */
export type PackRevealShare = { url: string };
export type PackRevealOptions = { share?: PackRevealShare | null };

const RC: Record<number, string> = { 0: '#8aa0b4', 1: '#4da3ff', 2: '#b06bff', 3: '#ffcc3d', 4: '#35e0e0' };
const TIER_LABEL = ['COMMON', 'RARE', 'EPIC', 'LEGENDARY', 'RELIC'];
const RELIC_TIER = 4;
const HERO_TIER = 2; // Epic+ — what a big pack swipes through

/* "120 INC · $4.80" — INC to the cent only when small, USD always to the cent; the
   HTML is built from numbers only (nothing member-written goes in). */
const fmtInc = (v: number) =>
  (v >= 100 ? Math.round(v) : Math.round(v * 100) / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 });
const fmtUsd = (v: number) => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hasValue = (c: RevealCard) => typeof c.valueInc === 'number' && c.valueInc > 0;
/* THE APPRAISAL. The value is not printed on the card — it is READ OFF it: when a card
   comes to the top a scan line sweeps down the art, and the readout under the card,
   which was showing "SCANNING", rolls the figure in on odometer drums, digit by digit
   from the left, the dollars snapping in after the last drum locks. A card with no
   listing comes back "NO MARKET PRICE YET". The haul's total rolls in the same way,
   bigger. (Numbers only go into this markup; no member text.) */
const valueHtml = (c: RevealCard, cls: string) =>
  '<div class="' + cls + ' pk-appr' + (hasValue(c) ? '' : ' none') + '" data-inc="' + (hasValue(c) ? (c.valueInc as number) : '') +
  '" data-usd="' + (hasValue(c) && typeof c.valueUsd === 'number' ? c.valueUsd : '') + '">' +
  '<div class="pk-appr-head"><span class="pk-appr-k">Appraisal</span><span class="pk-appr-state wait">Scanning</span></div>' +
  '<div class="pk-appr-row"><span class="pk-appr-inc od-num"></span><span class="pk-appr-unit"></span><span class="pk-appr-usd"></span></div>' +
  '</div>';
/* A number on odometer drums: each digit a strip of 0–9 (twice over, so every drum turns
   at least a full revolution) that rolls to rest on its figure; drums lock left to right.
   Separators (, .) are still. `instant` skips the roll (reduced motion). */
const odometer = (target: HTMLElement, text: string, instant: boolean, stepMs = 85) => {
  target.innerHTML = '';
  let i = 0;
  const DIGITS = '01234567890123456789';
  for (const ch of text) {
    if (/\d/.test(ch)) {
      const d = Number(ch);
      const od = document.createElement('span');
      od.className = 'od';
      const strip = document.createElement('span');
      strip.className = 'od-strip';
      strip.innerHTML = DIGITS.split('').map((x) => '<span class="od-d">' + x + '</span>').join('');
      od.appendChild(strip);
      target.appendChild(od);
      const rest = 'translateY(-' + (10 + d) + 'em)';
      if (instant) {
        strip.style.transition = 'none';
        strip.style.transform = rest;
      } else {
        strip.style.transitionDelay = i * stepMs + 'ms';
        // two frames so the first paint is the 0 at rest and the roll is a transition
        requestAnimationFrame(() => requestAnimationFrame(() => { strip.style.transform = rest; }));
      }
      i++;
    } else {
      const sep = document.createElement('span');
      sep.className = 'od-sep';
      sep.textContent = ch;
      target.appendChild(sep);
    }
  }
  return i; // drums
};
const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const ROLL_MS = 900; // one drum's roll (matches .od-strip's transition)
/* the whole pull: the sum of the known values (a card with no listing adds nothing) */
const haulValue = (cards: RevealCard[]) => {
  let inc = 0, usd = 0, priced = 0, usdKnown = true;
  for (const c of cards) {
    if (!hasValue(c)) continue;
    priced++;
    inc += c.valueInc as number;
    if (typeof c.valueUsd === 'number') usd += c.valueUsd;
    else usdKnown = false;
  }
  return { inc, usd: usdKnown ? usd : null, priced };
};

/* Card names are stored lowercase ("luxor", "mike winkelmann (beeple)"); shown with each
   word capitalised — a COPY of CardInspectModal.cardTitle (the arena bundle cannot import
   React code); keep the two identical. */
export const cardTitle = (s: string) => String(s || '').replace(/(^|[\s(\-\/.])([a-z])/g, (m, p, c) => p + c.toUpperCase());
const esc = (s: string) =>
  String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Random order, but with all relics moved to the END (revealed last). The shuffle
// keeps the non-relics and relics each internally random; the partition only pulls
// the relics to the back so the rarest pull is always the finale.
const shuffleRelicsLast = (input: RevealCard[]): RevealCard[] => {
  const a = input.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = a[i];
    a[i] = a[j];
    a[j] = t;
  }
  const rest = a.filter((c) => c.tier < RELIC_TIER);
  const relics = a.filter((c) => c.tier >= RELIC_TIER);
  return rest.concat(relics);
};

type Part = { x: number; y: number; vx: number; vy: number; life: number; decay: number; size: number; color: string };

export function mountPackReveal(root: HTMLElement, cards: RevealCard[], onDone: () => void, opts?: PackRevealOptions): PackRevealHandle {
  cards = shuffleRelicsLast(cards);
  const share = opts && opts.share && /^https?:\/\//.test(String(opts.share.url)) ? { url: String(opts.share.url) } : null;

  // Big packs (legacy 400-card subs) would be a slog to swipe one card at a time, so
  // for > 50 cards we swipe only the valuable pulls (Epic+ = tier >= 2, unique by art)
  // — with relics still at the very back — and the full haul lands in the summary
  // grid. Smaller packs swipe every card. This is viewport-independent now (the swipe
  // reads well on desktop too). MOBILE only tunes the particle/canvas budget below.
  const MOBILE =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 560px)').matches;
  const BIG_PACK = cards.length > 50;
  const swipeSet: RevealCard[] = BIG_PACK
    ? cards.filter((c, i) => c.tier >= HERO_TIER && cards.findIndex((o) => o.art === c.art) === i)
    : cards.slice();

  root.classList.add('pkr-root');
  root.style.setProperty('--incrypt-mark', `url("${INCRYPT_MARK}")`);

  root.innerHTML = `
    <canvas id="packFx"></canvas>
    <div id="packVignette"></div>
    <div id="packStage">
      <div id="packSealed">
        <div class="pk-rays"></div><div class="pk-foil"></div><div class="pk-grain"></div>
        <div class="pk-tear"><span class="pk-tearlbl">TAP TO OPEN &#9656;</span></div>
        <div class="pk-logo">
          <div class="pk-seal"><img id="pk-mark-img" class="pk-markimg" alt="INCRYPT"></div>
          <div class="pk-wordtxt">INCRYPT</div>
          <span class="pk-sub">GENESIS PACK &middot; SHADOWS OF SATOSHI</span>
        </div>
        <div class="pk-genesis">SEALED &middot; YOUR CLAIM</div>
        <span class="pk-corner tl"></span><span class="pk-corner tr"></span>
        <span class="pk-corner bl"></span><span class="pk-corner br"></span>
        <div class="pk-shine"></div><div class="pk-gloss"></div>
      </div>
      <div id="packCards"></div>
    </div>
    <div id="packHud">
      <div id="packPrompt">TAP TO OPEN</div>
      <div id="packButtons" style="display:none">
        <button class="pbtn ghost" id="pack-showall" type="button" style="display:none">SKIP TO SUMMARY &#9656;</button>
        <button class="pbtn share" id="pack-share" type="button" style="display:none">SHARE YOUR PULL</button>
        <button class="pbtn primary" id="pack-again" type="button" style="display:none">CONTINUE &#9656;</button>
      </div>
    </div>`;

  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const markImg = $<HTMLImageElement>('#pk-mark-img');
  if (markImg) markImg.src = INCRYPT_MARK;

  const fx = $<HTMLCanvasElement>('#packFx');
  let state: 'sealed' | 'opening' | 'reveal' | 'done' = 'sealed';
  let parts: Part[] = [];
  let raf = 0;
  let destroyed = false;
  let haulShown = false;
  let haulCleanup: (() => void) | null = null;
  let modalClose: (() => void) | null = null;

  // --- swipe-deck state ---
  let deckNodes: HTMLElement[] = [];
  let deckIdx = 0;
  let lastCelebrated = -1;
  const DECK_DEPTH = 4; // cards kept visible behind the top for the "stack" look

  // MOBILE also caps the canvas/particle work: iOS Safari kills a tab under memory
  // pressure, and a full-viewport particle canvas at high DPR with per-particle
  // shadowBlur is the heaviest thing here — so on phones cap resolution, thin the
  // particles, and drop the blur.
  const best = cards.length ? cards.reduce((bi, c, i, a) => (c.tier > a[bi].tier ? i : bi), 0) : -1;
  const glow = (t: number) =>
    t >= 3 ? RC[t] : t === 2 ? 'rgba(176,107,255,.55)' : t === 1 ? 'rgba(77,163,255,.4)' : 'rgba(138,160,180,.25)';

  function sizeFx() {
    const dpr = Math.min(window.devicePixelRatio || 1, MOBILE ? 1.5 : 2);
    fx.width = Math.round(window.innerWidth * dpr);
    fx.height = Math.round(window.innerHeight * dpr);
    fx.style.width = window.innerWidth + 'px';
    fx.style.height = window.innerHeight + 'px';
    const ctx = fx.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  function burst(x: number, y: number, color: string, count: number, spread = 4) {
    if (MOBILE) count = Math.round(count * 0.45);
    for (let k = 0; k < count; k++) {
      const a = Math.random() * Math.PI * 2;
      const sp = Math.random() * spread + 1.5;
      parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.6, life: 1, decay: 0.012 + Math.random() * 0.02, size: 1.5 + Math.random() * 3.5, color });
    }
    const cap = MOBILE ? 350 : 900;
    if (parts.length > cap) parts.splice(0, parts.length - cap);
  }
  function loop() {
    const ctx = fx.getContext('2d');
    if (!ctx) return;
    const step = () => {
      if (destroyed) return;
      const W = window.innerWidth, H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);
      for (let k = parts.length - 1; k >= 0; k--) {
        const p = parts[k];
        p.vy += 0.12; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.life -= p.decay;
        if (p.life <= 0) { parts.splice(k, 1); continue; }
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        if (!MOBILE) { ctx.shadowColor = p.color; ctx.shadowBlur = 8; }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size * (0.5 + p.life * 0.8), 0, 6.283); ctx.fill();
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      raf = requestAnimationFrame(step);
    };
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(step);
  }

  // ---- Swipe deck ----------------------------------------------------------
  function cardNode(c: RevealCard): HTMLElement {
    const rc = RC[c.tier] || RC[0];
    const el = document.createElement('div');
    el.className = 'pk-swipe tier-' + c.tier + (c.tier >= HERO_TIER ? ' hi' : '') + (c.tier >= RELIC_TIER ? ' relic' : '');
    el.style.setProperty('--rc', rc);
    el.style.setProperty('--rglow', glow(c.tier));
    const rarity = (c.rarity ? c.rarity.toUpperCase() : TIER_LABEL[c.tier]) || '';
    // data-src (not src) — art is loaded only for the near cards (setNearArt).
    el.innerHTML =
      '<div class="pk-swipe-face">' +
      '<div class="pk-swipe-art"><img alt="" decoding="async" data-src="' + esc(c.art) + '" onload="this.classList.add(\'on\')" onerror="this.remove()"><div class="pk-sheen"></div><div class="pk-holo"></div><div class="pk-scan"></div></div>' +
      '<div class="pk-swipe-info"><div class="pk-swipe-nm">' + esc(cardTitle(c.name)) + '</div>' +
      '<div class="pk-swipe-rar">' + esc(rarity) + '</div></div>' +
      (c.tier >= HERO_TIER ? '<div class="pk-swipe-badge">' + esc(rarity) + '</div>' : '') +
      '</div>' +
      // the WORTH plate sits BELOW the card as its own box (it rides with the card's
      // drag, being its child); only the top card's shows (opacity by --depth)
      valueHtml(c, 'pk-swipe-val');
    return el;
  }

  function setNearArt() {
    for (let i = deckIdx; i < deckNodes.length && i <= deckIdx + DECK_DEPTH + 1; i++) {
      const img = deckNodes[i].querySelector('img');
      if (img) {
        const s = img.getAttribute('data-src');
        if (s && img.getAttribute('src') !== s) img.setAttribute('src', s);
      }
    }
  }

  function positionStack() {
    for (let i = 0; i < deckNodes.length; i++) {
      const el = deckNodes[i];
      if (i < deckIdx) continue; // already flicked away — leave it animating out
      const depth = i - deckIdx;
      if (depth > DECK_DEPTH) { el.style.display = 'none'; continue; }
      el.style.display = '';
      el.classList.toggle('top', depth === 0);
      el.style.zIndex = String(1000 - depth);
      el.style.setProperty('--depth', String(depth));
      if (depth !== 0) el.style.transform = ''; // rest pose from CSS --depth; top is drag-driven
    }
    setNearArt();
    const top = deckNodes[deckIdx];
    if (top) attachDrag(top);
    updateProgress();
    celebrateTop();
  }

  // Celebrate the card you SEE (the newly-revealed top), not the one you flick away —
  // so the shake lands on the legendary/relic in front of you. Epic+ gets a particle
  // burst; legendary+ (tier >= 3) also shakes the stage. Fires once per revealed card.
  /* Read the top card's value off it: the scan sweeps the art, the readout rolls the
     figure in, the dollars snap on after the last drum, and an Epic+ card's readout gets
     its own burst as the number lands. Once per card. */
  function appraise(el: HTMLElement, c: RevealCard) {
    const plate = el.querySelector<HTMLElement>('.pk-appr');
    if (!plate || plate.dataset.done) return;
    plate.dataset.done = '1';
    const face = el.querySelector<HTMLElement>('.pk-swipe-face');
    const state = plate.querySelector<HTMLElement>('.pk-appr-state');
    const incEl = plate.querySelector<HTMLElement>('.pk-appr-inc');
    const unit = plate.querySelector<HTMLElement>('.pk-appr-unit');
    const usdEl = plate.querySelector<HTMLElement>('.pk-appr-usd');
    const instant = reducedMotion();
    if (face && !instant) face.classList.add('scan');
    const read = () => {
      plate.classList.add('on');
      if (!hasValue(c)) {
        if (state) { state.textContent = 'No market price yet'; state.classList.remove('wait'); }
        if (incEl) incEl.textContent = '—';
        return;
      }
      if (state) { state.textContent = 'Market value'; state.classList.remove('wait'); }
      const drums = incEl ? odometer(incEl, fmtInc(c.valueInc as number), instant) : 0;
      if (unit) unit.textContent = 'INC';
      const settle = instant ? 0 : ROLL_MS + drums * 85;
      setTimeout(() => {
        plate.classList.add('locked');
        if (usdEl && typeof c.valueUsd === 'number') { usdEl.textContent = fmtUsd(c.valueUsd); usdEl.classList.add('pop'); }
        if (c.tier >= HERO_TIER) {
          const r = plate.getBoundingClientRect();
          burst(r.left + r.width / 2, r.top + r.height / 2, RC[c.tier] || RC[0], [0, 0, 14, 26, 44][c.tier] || 14, 2.5 + c.tier * 0.6);
        }
      }, settle);
    };
    setTimeout(read, instant ? 0 : 620); // the scan takes 0.6 s to reach the bottom edge
  }

  function celebrateTop() {
    if (deckIdx >= swipeSet.length || lastCelebrated === deckIdx) return;
    lastCelebrated = deckIdx;
    const c = swipeSet[deckIdx];
    const node = deckNodes[deckIdx];
    if (node) appraise(node, c);
    if (c.tier < HERO_TIER) return;
    const el = deckNodes[deckIdx];
    const rect = el ? el.getBoundingClientRect() : null;
    const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const cy = rect ? rect.top + rect.height / 2 : window.innerHeight * 0.42;
    burst(cx, cy, RC[c.tier] || RC[0], [10, 22, 40, 70, 130][c.tier] || 20, 3 + c.tier);
    if (c.tier >= 3) {
      const st = $('#packStage');
      st.classList.add('shake');
      setTimeout(() => st.classList.remove('shake'), 420);
    }
  }

  function updateProgress() {
    const p = $('#packPrompt');
    if (!p) return;
    const total = swipeSet.length;
    const cur = Math.min(deckIdx + 1, total);
    if (deckIdx >= total) {
      p.textContent = '';
      return;
    }
    /* On a big pack, SAY that this is a filtered subset.
     *
     * It used to read "TAP/SWIPE • 1 / 71" with nothing to explain that 71 was
     * the Epic+ pick of a 400-card pack. A member swiped 71 cards, saw 71 / 71,
     * concluded he had been short-changed 329 of them, and opened a ticket. The
     * full haul WAS there — the summary grid behind this reads "400 cards · 286
     * unique" and is built from every card — but he never got far enough to
     * disbelieve the counter, and there is no reason he should have had to. */
    p.textContent = BIG_PACK
      ? 'TAP/SWIPE • ' + cur + ' / ' + total + ' BEST PULLS · ALL ' + cards.length + ' AT THE END'
      : 'TAP/SWIPE • ' + cur + ' / ' + total;
  }

  function attachDrag(el: HTMLElement) {
    if (el.dataset.drag) return;
    el.dataset.drag = '1';
    let sx = 0, dx = 0, dragging = false, moved = false;
    const isTop = () => deckNodes[deckIdx] === el && !el.classList.contains('flick');
    const onDown = (e: PointerEvent) => {
      if (!isTop()) return;
      dragging = true; moved = false; sx = e.clientX; dx = 0;
      try { el.setPointerCapture(e.pointerId); } catch (err) {}
      el.style.transition = 'none';
      el.classList.add('grab');
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      dx = e.clientX - sx;
      if (Math.abs(dx) > 4) moved = true;
      const t = Math.max(-1, Math.min(1, dx / 130));
      el.style.transform = 'translateX(' + dx + 'px) translateY(' + Math.abs(dx) * 0.03 + 'px) rotateZ(' + (dx * 0.035) + 'deg) rotateY(' + (dx * 0.05) + 'deg)';
      el.style.setProperty('--tilt', t.toFixed(3));
    };
    const onUp = () => {
      if (!dragging) return; dragging = false;
      el.classList.remove('grab');
      el.style.transition = '';
      if (Math.abs(dx) > 72) flick(el, dx > 0 ? 1 : -1);
      else { el.style.transform = ''; el.style.removeProperty('--tilt'); }
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    // A plain tap (no drag) advances too — flick it to the right.
    el.addEventListener('click', () => { if (!moved && isTop()) flick(el, 1); });
  }

  function flick(el: HTMLElement, dir: number) {
    if (el.classList.contains('flick')) return;
    el.classList.add('flick');
    el.dataset.drag = ''; // stop it responding to further input
    // No celebration here — it fires on REVEAL of the next card (celebrateTop),
    // so the shake lands on the legendary/relic you're looking at, not the flick.
    el.style.transition = 'transform .42s cubic-bezier(.36,.07,.3,1), opacity .42s ease';
    el.style.transform = 'translateX(' + dir * 130 + 'vw) rotateZ(' + dir * 26 + 'deg)';
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';
    const gone = el;
    setTimeout(() => { if (gone.parentNode) gone.parentNode.removeChild(gone); }, 480);

    deckIdx++;
    if (deckIdx >= swipeSet.length) {
      setTimeout(() => { if (!destroyed) showHaul(); }, 380);
    } else {
      positionStack();
    }
  }

  function buildSwipeDeck() {
    const cc = $('#packCards');
    cc.className = 'deck';
    cc.innerHTML = '<div class="pk-deck"></div>';
    const stack = cc.querySelector('.pk-deck') as HTMLElement;
    deckIdx = 0;
    deckNodes = swipeSet.map((c) => {
      const el = cardNode(c);
      stack.appendChild(el);
      return el;
    });
    positionStack();
    state = 'reveal';
    $('#packButtons').style.display = 'flex';
    // Let the player jump straight to the full summary at any point.
    $('#pack-showall').style.display = swipeSet.length > 1 ? '' : 'none';
  }

  // Big-pack finale (now shown for EVERY pack): the whole haul as a compact, face-up
  // grid. Images load ONLY near the viewport and drop when scrolled away, so a
  // 400-card grid keeps just a screenful of decoded art resident.
  function showHaul() {
    if (haulShown) return;
    haulShown = true;
    state = 'done';
    $('#packPrompt').style.display = 'none';
    $('#pack-showall').style.display = 'none';
    $('#packButtons').style.display = 'flex';
    const cc = $('#packCards');
    cc.className = 'haul';
    cc.innerHTML = '';
    // Dedupe identical cards (same art) into ONE tile with a ×N badge, best-first.
    const groups: { c: RevealCard; n: number }[] = [];
    const byArt: Record<string, { c: RevealCard; n: number }> = {};
    cards.forEach((c) => {
      const g = byArt[c.art];
      if (g) g.n++;
      else {
        byArt[c.art] = { c, n: 1 };
        groups.push(byArt[c.art]);
      }
    });
    groups.sort((a, b) => b.c.tier - a.c.tier);
    const epicPlus = cards.filter((c) => c.tier >= HERO_TIER).length;
    const head = document.createElement('div');
    head.className = 'haul-head';
    const hv = haulValue(cards);
    const haulHit = hv.inc >= 1000 ? ' big' : '';
    head.innerHTML =
      '<div class="haul-title">YOUR HAUL</div><div class="haul-sub">' +
      cards.length +
      ' cards &middot; ' +
      groups.length +
      ' unique' +
      (epicPlus ? ' &middot; ' + epicPlus + ' Epic+' : '') +
      '</div>' +
      (hv.priced
        ? '<div class="haul-value' + haulHit + '"><span class="haul-value-k">Your pull is worth</span>' +
          '<div class="haul-value-row"><span class="haul-value-inc od-num" data-to="' + hv.inc + '"></span><small class="haul-value-unit">INC</small>' +
          (hv.usd != null ? '<span class="haul-value-usd" data-to="' + hv.usd + '"></span>' : '') + '</div>' +
          '<div class="haul-value-note">Based on average marketplace listings' +
          (hv.priced < cards.length ? ' &middot; ' + (cards.length - hv.priced) + ' card' + (cards.length - hv.priced === 1 ? '' : 's') + ' with no listing yet' : '') +
          '</div></div>'
        : '') +
      '<div class="haul-hint">Tap a card to enlarge &amp; tilt</div>';
    cc.appendChild(head);
    countUp(head);
    groups.forEach((g) => {
      const c = g.c;
      const el = document.createElement('div');
      el.className = 'pmini' + (c.tier >= HERO_TIER ? ' hero' : '') + (c.tier >= RELIC_TIER ? ' relic' : '');
      el.style.setProperty('--rc', RC[c.tier] || RC[0]);
      el.innerHTML =
        '<div class="pmart"><img alt="" decoding="async" data-src="' +
        esc(c.art) +
        '"></div>' +
        (c.tier >= RELIC_TIER ? '<div class="pm-holo"></div>' : '') +
        (g.n > 1 ? '<div class="pmqty">x' + g.n + '</div>' : '') +
        (hasValue(c) ? '<div class="pmval">' + fmtInc(c.valueInc as number) + ' INC</div>' : '');
      el.addEventListener('click', () => openCardModal(g));
      cc.appendChild(el);
    });
    windowHaulImages(cc);
    $('#pack-again').style.display = '';
    /* The buttons move INTO the haul, as a full-width last row under the cards, so
       CONTINUE scrolls with the collection and never sits over a card (the HUD is a
       fixed overlay at the stage's bottom edge, which on a short haul landed on the
       last row — owner, 2026-10-22). Same element, so the click handler stays. */
    const foot = document.createElement('div');
    foot.className = 'haul-foot';
    cc.appendChild(foot); // attached FIRST: $() searches the root, and a detached foot hides its buttons from it
    foot.appendChild($('#packButtons'));
    if (share) {
      // SHARE YOUR PULL: the phone's share sheet where there is one, else the link is
      // copied; if even that is refused (an embedded view) the link is shown to copy
      const shareBtn = $('#pack-share');
      shareBtn.style.display = '';
      const note = document.createElement('div');
      note.className = 'haul-share-note';
      foot.appendChild(note);
      shareBtn.addEventListener('click', () => shareHaul(share.url, note));
    }
    $('#packHud').style.display = 'none';
    const b = cards[best];
    if (b && b.tier >= 3)
      burst(window.innerWidth / 2, window.innerHeight * 0.3, RC[b.tier] || RC[0], 90, 7);
  }

  /* Share the pull: navigator.share (a phone's sheet) → clipboard → a box with the
     link selected. The text is ours; the URL is the server's. */
  function shareHaul(url: string, note: HTMLElement) {
    const n = cards.length;
    const text = 'I just pulled ' + n + ' card' + (n === 1 ? '' : 's') + ' on INCRYPT — see what I got:';
    const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string; url?: string }) => Promise<void> };
    const showLink = () => {
      note.innerHTML = '<input class="haul-share-link" type="text" readonly value="' + esc(url) + '"><div class="haul-share-hint">Copy your link</div>';
      const inp = note.querySelector<HTMLInputElement>('input');
      if (inp) { inp.focus(); inp.select(); }
    };
    const copied = () => { note.textContent = 'LINK COPIED — paste it anywhere'; };
    const copy = () => {
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(copied, showLink);
      else showLink();
    };
    if (nav.share) nav.share({ title: 'My INCRYPT pull', text, url }).catch((e: unknown) => {
      // a dismissed sheet is not a failure; anything else falls back to copying
      if (!(e && (e as { name?: string }).name === 'AbortError')) copy();
    });
    else copy();
  }

  /* The haul's total rolls in on the drums — the INC first, the dollars after the last
     drum locks — then the plate lights and, from 1,000 INC, bursts. A cut under reduced
     motion. Numbers only — no member text. */
  function countUp(head: HTMLElement) {
    const incEl = head.querySelector<HTMLElement>('.haul-value-inc');
    const usdEl = head.querySelector<HTMLElement>('.haul-value-usd');
    const plate = head.querySelector<HTMLElement>('.haul-value');
    if (!incEl || !plate) return;
    const toInc = Number(incEl.dataset.to) || 0;
    const toUsd = usdEl ? Number(usdEl.dataset.to) || 0 : 0;
    const instant = reducedMotion();
    const drums = odometer(incEl, fmtInc(toInc), instant, 110);
    const settle = instant ? 0 : ROLL_MS + drums * 110;
    setTimeout(() => {
      if (usdEl) {
        const n = odometer(usdEl, fmtUsd(toUsd).replace('$', ''), instant, 70);
        setTimeout(() => plate.classList.add('lit'), instant ? 0 : ROLL_MS + n * 70);
      } else plate.classList.add('lit');
      if (toInc >= 1000 && !instant) {
        const r = plate.getBoundingClientRect();
        burst(r.left + r.width / 2, r.top + r.height / 2, toInc >= 10000 ? '#ffd36b' : '#38e8ff', 60, 5);
      }
    }, settle);
  }

  // Bound decoded-image memory in the haul: keep art loaded only for tiles near the
  // viewport, dropped once scrolled well away. A throttled scroll handler over cached
  // tile geometry — deliberately NOT IntersectionObserver, whose callbacks can
  // silently never fire in some embedded/headless webviews (observed here).
  function windowHaulImages(container: HTMLElement) {
    const imgs = Array.prototype.slice.call(
      container.querySelectorAll('.pmart img')
    ) as HTMLImageElement[];
    if (!imgs.length) return;
    const geo = imgs.map((im) => {
      const tile = (im.closest('.pmini') as HTMLElement) || im;
      return { im, top: tile.offsetTop, bottom: tile.offsetTop + tile.offsetHeight };
    });
    const MARGIN = 500;
    let ticking = false;
    const apply = () => {
      ticking = false;
      const viewTop = container.scrollTop - MARGIN;
      const viewBottom = container.scrollTop + container.clientHeight + MARGIN;
      for (let k = 0; k < geo.length; k++) {
        const g = geo[k], im = g.im;
        const near = g.bottom >= viewTop && g.top <= viewBottom;
        if (near) {
          const s = im.getAttribute('data-src');
          if (s && im.getAttribute('src') !== s) { im.src = s; im.classList.add('on'); }
        } else if (im.getAttribute('src')) {
          im.removeAttribute('src');
          im.classList.remove('on');
        }
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    haulCleanup = () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    apply();
  }

  // Tap a haul tile → the card enlarged, and TILTABLE: drag it to rotate in 3D
  // (Pokémon-style) and the foil sheen tracks the tilt. Tap the backdrop / × to close.
  function openCardModal(g: { c: RevealCard; n: number }) {
    if (modalClose) modalClose();
    const c = g.c;
    const rarity = (c.rarity ? c.rarity.toUpperCase() : TIER_LABEL[c.tier]) || '';
    const modal = document.createElement('div');
    modal.className = 'pk-modal';
    modal.style.setProperty('--rc', RC[c.tier] || RC[0]);
    modal.innerHTML =
      '<div class="pk-modal-card' + (c.tier >= RELIC_TIER ? ' relic' : '') + '">' +
      '<div class="pk-modal-tilt">' +
      '<div class="pk-modal-art"><img alt="" src="' + esc(c.art) + '"><div class="pk-sheen"></div><div class="pk-holo"></div></div>' +
      '</div>' +
      '<div class="pk-modal-info"><div class="pk-modal-name">' + esc(cardTitle(c.name)) + '</div>' +
      '<div class="pk-modal-rar">' + esc(rarity) +
      (g.n > 1 ? ' &middot; &times;' + g.n : '') + '</div></div>' +
      '<div class="pk-modal-worth">' + valueHtml(c, 'pk-modal-val') +
      (g.n > 1 && hasValue(c) ? '<div class="pk-modal-val-all">&times;' + g.n + ' = ' + fmtInc((c.valueInc as number) * g.n) + ' INC' +
        (typeof c.valueUsd === 'number' ? ' &middot; ' + fmtUsd(c.valueUsd * g.n) : '') + '</div>' : '') + '</div>' +
      '<button class="pk-modal-close" type="button" aria-label="Close">CLOSE</button>' +
      '</div>';
    const worth = modal.querySelector<HTMLElement>('.pk-modal-val');
    if (worth) {
      worth.dataset.done = '1';
      worth.classList.add('on');
      const st = worth.querySelector<HTMLElement>('.pk-appr-state');
      if (hasValue(c)) {
        if (st) { st.textContent = 'Market value'; st.classList.remove('wait'); }
        const incEl = worth.querySelector<HTMLElement>('.pk-appr-inc');
        const drums = incEl ? odometer(incEl, fmtInc(c.valueInc as number), reducedMotion(), 60) : 0;
        const unit = worth.querySelector<HTMLElement>('.pk-appr-unit');
        if (unit) unit.textContent = 'INC';
        const usdEl = worth.querySelector<HTMLElement>('.pk-appr-usd');
        setTimeout(() => {
          worth.classList.add('locked');
          if (usdEl && typeof c.valueUsd === 'number') { usdEl.textContent = fmtUsd(c.valueUsd); usdEl.classList.add('pop'); }
        }, reducedMotion() ? 0 : ROLL_MS + drums * 60);
      } else {
        if (st) { st.textContent = 'No market price yet'; st.classList.remove('wait'); }
        const incEl = worth.querySelector<HTMLElement>('.pk-appr-inc');
        if (incEl) incEl.textContent = '—';
      }
    }
    const close = () => {
      if (modalClose !== close) return;
      modalClose = null;
      document.removeEventListener('keydown', onKey);
      modal.classList.remove('on');
      setTimeout(() => { if (modal.parentNode) modal.parentNode.removeChild(modal); }, 200);
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') close(); };
    // Backdrop / × close, but a drag-to-tilt on the card must NOT close.
    modal.addEventListener('click', close);
    const tilt = modal.querySelector('.pk-modal-tilt') as HTMLElement;
    if (tilt) {
      let dragging = false, moved = false, cx = 0, cy = 0;
      tilt.addEventListener('pointerdown', (e) => {
        dragging = true; moved = false;
        const r = tilt.getBoundingClientRect();
        cx = r.left + r.width / 2; cy = r.top + r.height / 2;
        try { tilt.setPointerCapture((e as PointerEvent).pointerId); } catch (err) {}
        tilt.style.transition = 'none';
        e.stopPropagation();
      });
      tilt.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const pe = e as PointerEvent;
        const nx = Math.max(-1, Math.min(1, (pe.clientX - cx) / 150));
        const ny = Math.max(-1, Math.min(1, (pe.clientY - cy) / 200));
        if (Math.abs(pe.clientX - cx) > 3 || Math.abs(pe.clientY - cy) > 3) moved = true;
        tilt.style.transform = 'rotateY(' + (nx * 22) + 'deg) rotateX(' + (-ny * 22) + 'deg)';
        tilt.style.setProperty('--tilt', nx.toFixed(3));
      });
      const end = () => {
        if (!dragging) return; dragging = false;
        tilt.style.transition = '';
        tilt.style.transform = '';
        tilt.style.removeProperty('--tilt');
      };
      tilt.addEventListener('pointerup', end);
      tilt.addEventListener('pointercancel', end);
      // Swallow the click after a tilt-drag so the modal doesn't close.
      tilt.addEventListener('click', (e) => { if (moved) e.stopPropagation(); });
    }
    document.addEventListener('keydown', onKey);
    modalClose = close;
    root.appendChild(modal);
    setTimeout(() => modal.classList.add('on'), 10);
  }

  function open() {
    if (state !== 'sealed') return;
    state = 'opening';
    const sealed = $('#packSealed');
    burst(window.innerWidth / 2, window.innerHeight * 0.45, '#7fb0ff', 90, 7);
    const st = $('#packStage'); st.classList.add('shake'); setTimeout(() => st.classList.remove('shake'), 500);
    sealed.classList.add('rip');
    $('#packPrompt').textContent = 'TAP OR SWIPE TO REVEAL';
    setTimeout(() => {
      if (destroyed) return;
      sealed.style.display = 'none';
      // Nothing to swipe (e.g. a big pack with no Epic+ pulls) — straight to the haul.
      if (swipeSet.length === 0) { showHaul(); return; }
      buildSwipeDeck();
    }, 560);
  }

  function done() { if (destroyed) return; destroy(); onDone(); }
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    cancelAnimationFrame(raf);
    if (haulCleanup) { haulCleanup(); haulCleanup = null; }
    if (modalClose) { modalClose(); modalClose = null; }
    parts = [];
    deckNodes = [];
    try { fx.width = 0; fx.height = 0; } catch (e) {}
    window.removeEventListener('resize', onResize);
    root.classList.remove('pkr-root');
    root.style.removeProperty('--incrypt-mark');
    root.innerHTML = '';
  }

  const onResize = () => sizeFx();
  window.addEventListener('resize', onResize);
  $('#packSealed').addEventListener('click', open);
  $('#pack-showall').addEventListener('click', showHaul);
  $('#pack-again').addEventListener('click', done);

  sizeFx();
  loop();
  return { destroy };
}
